import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { ModalName } from './modal-values.service';
import { AllModalInformation, ModalProperties } from '../../models/Modal.model';

@Injectable({
    providedIn: 'root'
})
export class BootstrapModalService {
    public openEventBroadcaster: ReplaySubject<any> = new ReplaySubject(1);
    public closeEventBroadcaster: Subject<any> = new Subject();
    public footerTemplate$ = new BehaviorSubject<TemplateRef<any> | null>(null);
    private animationStatusBroadcaster = new BehaviorSubject<boolean>(false);

    private modalsBufferStorage: Record<ModalName, boolean> = {} as Record<ModalName, boolean>;
    private availableModalInfos: Partial<Record<ModalName, AllModalInformation>> = {};
    private modalProperties: ModalProperties = {
        activated: false,
        modalActivated: null as ModalName | null,
        modalOpening: false,
        isAnimating: false,
    };

    constructor() { }

    /**
     * Method used to close modal container
     */
    public close(newRoute?: string, options?: any) {
        // TODO change this if you want to count in the modal as a different route, but it is not
        // TODO needed right now and maybe will never be needed
        if (newRoute) {
            this.closeEventBroadcaster.next(newRoute);
        } else {
            this.closeEventBroadcaster.next({ modal: this.modalProperties.modalActivated, options });
        }
    }

    /**
     * Method used to open modal container
     * @param {ModalName} name Name of the component to be injected
     * @param {AllModalInformation} allModalInformation
     * * with the following properties
     * * containerOptions Modal styling options. Omit this param if none of the styling options are different from default ones:
     * * size?: ModalSize.MD
     * * backdropDismissable?: true
     * * buttonDismissable?: true
     * * exceptionClass?: string
     * * isFlow?: false
     * * reverseAnimation?: false
     * * contentOptions
     * * options
     * @param {boolean} override
    */
    public open(allModalInformation: AllModalInformation, override?: boolean) {
        this.availableModalInfos[allModalInformation.name] = allModalInformation;
        if (!this.modalProperties.activated || override) {
            if (override && this.modalProperties.modalActivated) {
                // * delete the data of the modal that is closing
                delete this.availableModalInfos[this.modalProperties.modalActivated as ModalName];
            }
            this.modalProperties.activated = true;
            this.modalProperties.modalActivated = allModalInformation.name;
            this.modalProperties.modalOpening = true;
            this.openEventBroadcaster.next(allModalInformation);
        }
    }

    public resetOnClose() {
        if (this.modalProperties.modalActivated) {
            delete this.availableModalInfos[this.modalProperties.modalActivated as ModalName];
        }
        this.modalProperties.activated = false;
        this.modalProperties.modalOpening = false;
        this.modalProperties.modalActivated = null;
        this.clearFooterTemplate();
    }

    public setFooterTemplate(template: TemplateRef<any>): void {
        this.footerTemplate$.next(template);
    }

    public clearFooterTemplate(): void {
        this.footerTemplate$.next(null);
    }

    public hasFooterTemplate(): boolean {
        return this.footerTemplate$.value != null;
    }

    public getPendingModal() {
        return this.modalProperties.modalActivated;
    }

    public getModalInfo(modal: ModalName) {
        return this.availableModalInfos[modal];
    }

    public getModalContentOptions(modal: ModalName): any {
        return this.availableModalInfos?.[modal]?.contentOptions ?? {};
    }

    public getIsModalOpened() {
        return this.modalProperties.activated;
    }

    public setBufferItem(key: ModalName, value: boolean) {
        this.modalsBufferStorage[key] = value;
    }

    public getBufferItem(key: ModalName) {
        return this.modalsBufferStorage[key];
    }

    public setIsAnimating(value: boolean) {
        this.modalProperties.isAnimating = value;
        this.animationStatusBroadcaster.next(value);
    }

    public getIsAnimating() {
        return this.modalProperties.isAnimating;
    }
}